import { Laptop } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { IssuedToken } from "@/components/IssuedToken";
import { useApp } from "@/lib/AppContext";
import { ApiError, type IssuedWorker, type WorkerNode } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";

/**
 * Settings → Recorder (CAP-5, UI-13).
 *
 * **This screen departs from the handoff, and the reason is a decision that was made
 * after it was drawn.** The prototype draws one switch — "Teams capture on this PC…
 * started from the worker tray" — which assumes capture is something the Worker does,
 * and something that only ever means Teams. M11.1 established the first cannot be:
 * capture runs on the owner's own PC, in front of the meeting, not on the GPU machine,
 * and it is a separate app with its own installer. The second is just as wrong: the
 * recorder mixes whatever the PC's own microphone and speakers carry, so it works the
 * same in front of Teams, Zoom, a phone call on speaker, or no call software at all —
 * naming one of them here would claim a narrower app than the one that ships. So this
 * section does for a recorder PC exactly what the handoff's own Worker section does for
 * a worker — register it, show the token once, list what is registered, revoke —
 * because that is the pattern the design already contains for "a machine this account
 * trusts".
 */
export function CaptureSettings({ summary }: { summary: string }) {
  const { api } = useApp();

  const [devices, setDevices] = useState<WorkerNode[] | null>(null);
  const [issued, setIssued] = useState<IssuedWorker | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setDevices(await api.listCaptureDevices());
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The recorders could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function register(event: React.FormEvent) {
    event.preventDefault();
    if (busy || name.trim().length === 0) return;

    setBusy(true);
    setProblem(null);

    try {
      setIssued(await api.registerCaptureDevice(name.trim()));
      setName("");
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The recorder could not be registered.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(device: WorkerNode) {
    setBusy(true);
    setProblem(null);

    try {
      await api.revokeCaptureDevice(device.id);
      await refresh();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The recorder could not be revoked.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Recorder
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem}
        </p>
      )}

      {issued && (
        <IssuedToken issued={issued} noun="recorder" onDismiss={() => setIssued(null)}>
          Paste it into Factorium Recorder on that PC, under <strong>Settings → Capture token</strong>,
          with this Server's address beside it.
        </IssuedToken>
      )}

      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          Register a recorder
        </h2>
        <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          Factorium Recorder runs in the system tray of your own PC and records the way a voice
          recorder on the desk would — the remote side from your speakers, your own voice from the
          microphone, mixed into one recording. Nothing joins a meeting, and nothing has to know it
          is running.
        </p>

        <form className="mt-[var(--space-4)] flex gap-[var(--space-3)]" onSubmit={(event) => void register(event)}>
          <label className="sr-only" htmlFor="capture-name">
            Recorder name
          </label>
          <input
            id="capture-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Work laptop"
            maxLength={80}
            className="min-w-0 flex-1 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-sm)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
          />
          <button
            type="submit"
            disabled={busy || name.trim().length === 0}
            className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            Register
          </button>
        </form>
      </section>

      <section>
        <h2 className="mb-[var(--space-3)] text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
          Registered recorders
        </h2>

        {devices === null && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
        )}

        {devices?.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            No recorder yet. Recordings arrive by upload until you register one — nothing here is
            required to use Factorium.
          </p>
        )}

        <ul className="flex flex-col gap-[var(--space-2)]">
          {devices?.map((device) => (
            <li
              key={device.id}
              className="flex items-center gap-[var(--space-4)] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)]"
            >
              <Laptop size={16} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
                  {device.name ?? "Unnamed recorder"}
                </p>
                <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                  {describeContact(device)}
                </p>
              </div>

              <span
                className={cn(
                  "shrink-0 rounded-[var(--radius-full)] px-[var(--space-2)] py-[2px] text-[length:var(--text-xs)] font-semibold",
                  device.status === "active"
                    ? "bg-[var(--surface-2)] text-[var(--text-primary)]"
                    : "bg-[var(--surface-2)] text-[var(--text-muted)]",
                )}
              >
                {device.status === "active" ? "Active" : "Revoked"}
              </span>

              {device.status === "active" && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void revoke(device)}
                  className="shrink-0 rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function describeContact(device: WorkerNode): string {
  if (device.status === "revoked")
    return `Revoked ${formatDate(device.revokedAt) || "recently"} — this recorder can no longer file recordings.`;

  return device.lastHeartbeatAt === null
    ? "Registered, but it has never connected. Has the token been pasted into Factorium Recorder?"
    : `Last connected ${formatDate(device.lastHeartbeatAt)}.`;
}
